import { useEffect, useMemo, useRef, useState } from "react";
import { useTheme } from "@emotion/react";
import { useChat } from "@ai-sdk/react";
import { getToolName } from "ai";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import CloseFullscreenRoundedIcon from "@mui/icons-material/CloseFullscreenRounded";
import OpenInFullRoundedIcon from "@mui/icons-material/OpenInFullRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import AmyAvatar from "./AmyAvatar";
import AmyLauncherButton from "./AmyLauncherButton";
import MDBox from "components/MDBox";
import { useMaterialUIController } from "context";
import { assistantAPI } from "api";
import { createAssistantChatTransport } from "./assistant/assistantChatTransport";
import ClarificationRenderer from "./assistant/ClarificationRenderer";
import ToolPartRenderer from "./assistant/ToolPartRenderer";
import { downloadChat } from "./assistant/chatExportUtils";
import {
  getLatestClarification,
  getMessageText,
  getLatestAssistantStatus,
  getToolParts,
  isThinkingMessage,
} from "./assistant/messageUtils";

const QUICK_PROMPTS = [
  "Show claim count by status for last 30 days",
  "Top 10 agents by claims handled this month",
  "How many claims are pending review right now?",
];

const ASSISTANT_POPUP_Z_INDEX = 1400;
const SIDENAV_WIDTH_FULL = 230;
const SIDENAV_WIDTH_MINI = 85;

function getFullscreenContainerSx(miniSidenav) {
  return ({ breakpoints, functions: { pxToRem } }) => ({
    position: "fixed",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: ASSISTANT_POPUP_Z_INDEX,
    p: 2,
    display: "flex",
    alignItems: "stretch",
    boxSizing: "border-box",
    [breakpoints.up("md")]: {
      left: pxToRem(miniSidenav ? SIDENAV_WIDTH_MINI : SIDENAV_WIDTH_FULL),
    },
  });
}

function createSessionId() {
  return `assistant-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function normalizeAssistantError(error) {
  if (!error) {
    return "We could not process that request. Please try again.";
  }

  const rawMessage = typeof error === "string" ? error : error.message || "";

  try {
    const parsed = JSON.parse(rawMessage);
    const details = Array.isArray(parsed?.detail) ? parsed.detail : parsed?.detail ? [parsed.detail] : [];

    if (details.length > 0) {
      const first = details[0];
      if (typeof first === "string") {
        return first;
      }
      if (first?.msg) {
        return first.msg;
      }
    }
  } catch (parseError) {
    // Fall through to generic handling.
  }

  if (rawMessage.includes("Not authenticated")) {
    return "Please sign in again to use the assistant.";
  }

  if (rawMessage) {
    return rawMessage;
  }

  return "We could not process that request. Please try again.";
}

function MessageCard({ message, isStreaming, onRerun, canRerun }) {
  const isAssistant = message.role === "assistant";
  const text = getMessageText(message);
  const statusPart = getLatestAssistantStatus(message);
  const toolParts = getToolParts(message).filter((part) => getToolName(part) !== "__assistant_status__");
  const showThinking = isThinkingMessage(message, isStreaming);

  return (
    <Card
      sx={{
        p: 1.25,
        borderRadius: "14px",
        border: "1px solid",
        borderColor: isAssistant ? "rgba(59,130,246,0.18)" : "rgba(15,23,42,0.12)",
        bgcolor: isAssistant ? "rgba(59,130,246,0.05)" : "background.paper",
      }}
      elevation={0}
    >
      <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="space-between" mb={0.5}>
        <Stack direction="row" spacing={0.75} alignItems="center">
          {isAssistant ? <AmyAvatar size={16} withBackground={false} animate={false} /> : null}
          <Typography fontWeight={600} sx={{ fontSize: "0.7rem" }} color="text.secondary">
            {isAssistant ? "Amy" : "You"}
          </Typography>
        </Stack>
        {!isAssistant && text && onRerun ? (
          <Tooltip title="Rerun this query">
            <span>
              <IconButton
                size="small"
                onClick={() => onRerun(message.id)}
                disabled={!canRerun}
                aria-label="Rerun this query"
                sx={{ p: 0.25 }}
              >
                <ReplayRoundedIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </span>
          </Tooltip>
        ) : null}
      </Stack>

      {text ? (
        <Typography sx={{ fontSize: "0.8rem", lineHeight: 1.45 }} whiteSpace="pre-wrap">
          {text}
        </Typography>
      ) : null}

      {isAssistant && isStreaming && statusPart?.label ? (
        <Card
          elevation={0}
          sx={{
            mt: text ? 0.75 : 0.25,
            px: 0.8,
            py: 0.5,
            borderRadius: "10px",
            border: "1px solid rgba(59,130,246,0.3)",
            bgcolor: "rgba(59,130,246,0.08)",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.65}>
            <CircularProgress size={10} />
            <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: "primary.main" }}>
              {statusPart.label}
            </Typography>
          </Stack>
        </Card>
      ) : null}

      {showThinking && !statusPart?.label ? (
        <Chip
          size="small"
          label="Thinking..."
          sx={{ mt: text ? 1 : 0, height: 22, fontSize: "0.7rem" }}
          color="default"
          variant="outlined"
        />
      ) : null}

      {toolParts.map((part) => (
        <ToolPartRenderer key={part.toolCallId} part={part} />
      ))}
    </Card>
  );
}

export default function AssistantChatExperience({ mode = "popup", onClose, claimId = null }) {
  const { palette } = useTheme();
  const { white } = palette;
  const [controller] = useMaterialUIController();
  const { miniSidenav } = controller;
  const resolvedClaimId = typeof claimId === "string" && claimId.trim() ? claimId.trim() : null;
  const [sessionId, setSessionId] = useState(() => createSessionId());
  const [assistantError, setAssistantError] = useState("");
  const [open, setOpen] = useState(mode === "page");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [input, setInput] = useState("");
  const [dismissedClarificationId, setDismissedClarificationId] = useState(null);
  const scrollRef = useRef(null);
  const sessionIdRef = useRef(sessionId);
  const claimIdRef = useRef(resolvedClaimId);

  sessionIdRef.current = sessionId;
  claimIdRef.current = resolvedClaimId;

  const transport = useMemo(
    () => createAssistantChatTransport({ sessionIdRef, claimIdRef }),
    []
  );

  const { messages, setMessages, sendMessage, status, error } = useChat({
    id: resolvedClaimId ? `claims-analytics-assistant-${resolvedClaimId}` : "claims-analytics-assistant",
    messages: [],
    transport,
    onError: (chatError) => {
      setAssistantError(normalizeAssistantError(chatError));
    },
  });

  const isRequestRunning = status === "submitted" || status === "streaming";
  const normalizedInput = typeof input === "string" ? input : "";
  const trimmedInput = normalizedInput.trim();
  const canSubmit = trimmedInput.length >= 1 && trimmedInput.length <= 2000 && !isRequestRunning;
  const activeClarification = useMemo(() => getLatestClarification(messages), [messages]);
  const visibleClarification =
    activeClarification && activeClarification.clarificationId !== dismissedClarificationId
      ? activeClarification
      : null;

  const warmSession = (nextSessionId) => {
    assistantAPI.warmSession(nextSessionId).catch(() => {});
  };

  useEffect(() => {
    const nextSessionId = createSessionId();
    setMessages([]);
    setAssistantError("");
    setInput("");
    setDismissedClarificationId(null);
    setSessionId(nextSessionId);
    warmSession(nextSessionId);
  }, [resolvedClaimId, setMessages]);

  useEffect(() => {
    if (mode === "popup" && !open) {
      return;
    }
    warmSession(sessionId);
  }, [open, mode, sessionId]);

  useEffect(() => {
    if (!open) {
      setIsFullscreen(false);
    }
  }, [open]);

  useEffect(() => {
    if (error) {
      setAssistantError(normalizeAssistantError(error));
    }
  }, [error]);

  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, isRequestRunning]);

  const resetConversation = () => {
    const nextSessionId = createSessionId();
    setMessages([]);
    setAssistantError("");
    setInput("");
    setDismissedClarificationId(null);
    setSessionId(nextSessionId);
    warmSession(nextSessionId);
  };

  const sendPrompt = async (prompt) => {
    const query = (typeof prompt === "string" ? prompt : "").trim();
    if (query.length < 1 || query.length > 2000 || isRequestRunning) {
      return;
    }

    setAssistantError("");
    setInput("");
    await sendMessage({ text: query });
  };

  const rerunQuery = async (messageId) => {
    if (isRequestRunning) {
      return;
    }

    const messageIndex = messages.findIndex((item) => item.id === messageId);
    if (messageIndex === -1) {
      return;
    }

    const message = messages[messageIndex];
    if (message.role !== "user") {
      return;
    }

    const query = getMessageText(message).trim();
    if (query.length < 1 || query.length > 2000) {
      return;
    }

    setAssistantError("");
    setInput("");
    setMessages(messages.slice(0, messageIndex));
    await sendMessage({ text: query });
  };

  const panel = (
    <Card
      sx={{
        width: isFullscreen
          ? "100%"
          : mode === "popup"
            ? { xs: "calc(100vw - 24px)", sm: 460 }
            : "100%",
        height: isFullscreen
          ? "100%"
          : mode === "popup"
            ? { xs: "calc(100vh - 24px)", sm: 700 }
            : "calc(100vh - 150px)",
        maxHeight: isFullscreen ? "100%" : "calc(100vh - 24px)",
        p: 2,
        borderRadius: isFullscreen ? "16px" : mode === "popup" ? "16px" : "20px",
        boxShadow: mode === "popup" ? "0 20px 45px rgba(2,6,23,0.22)" : "0 12px 30px rgba(2,6,23,0.12)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <AmyAvatar size={36} />
          <Stack spacing={0.15}>
            <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
              Amy
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Ask natural-language analytics questions
            </Typography>
          </Stack>
        </Stack>

        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Download chat as PDF">
            <span>
              <IconButton
                size="small"
                onClick={() => downloadChat(messages, { claimId: resolvedClaimId })}
                disabled={messages.length === 0 || isRequestRunning}
                aria-label="Download chat"
              >
                <DownloadRoundedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Start new chat">
            <IconButton size="small" onClick={resetConversation}>
              <RefreshRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={isFullscreen ? "Exit full screen" : "Full screen"}>
            <IconButton
              size="small"
              onClick={() => setIsFullscreen((value) => !value)}
              aria-label={isFullscreen ? "Exit full screen" : "Full screen"}
            >
              {isFullscreen ? (
                <CloseFullscreenRoundedIcon fontSize="small" />
              ) : (
                <OpenInFullRoundedIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
          {mode === "popup" ? (
            <Tooltip title="Close">
              <IconButton size="small" onClick={() => (onClose ? onClose() : setOpen(false))}>
                <span className="material-symbols-outlined">close</span>
              </IconButton>
            </Tooltip>
          ) : null}
        </Stack>
      </Stack>

      {assistantError ? (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {assistantError}
        </Alert>
      ) : null}

      <MDBox
        ref={scrollRef}
        sx={{
          flex: 1,
          overflowY: "auto",
          pr: 0.5,
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        {messages.length === 0 ? (
          <Card elevation={0} sx={{ p: 1.25, borderRadius: "14px", bgcolor: "rgba(59,130,246,0.06)" }}>
            <Typography sx={{ fontSize: "0.75rem", lineHeight: 1.4, mb: 0.75 }} color="text.secondary">
              Ask about trends, status distributions, aging buckets, customer breakdowns, or agent performance.
            </Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {QUICK_PROMPTS.map((prompt) => (
                <Chip
                  key={prompt}
                  label={prompt}
                  size="small"
                  onClick={() => sendPrompt(prompt)}
                  sx={{
                    height: 24,
                    fontSize: "0.7rem",
                    "& .MuiChip-label": { px: 1, py: 0 },
                  }}
                />
              ))}
            </Stack>
          </Card>
        ) : null}

        {messages.map((message, index) => {
          const isLastAssistant =
            message.role === "assistant" && index === messages.length - 1 && isRequestRunning;

          return (
            <MessageCard
              key={message.id}
              message={message}
              isStreaming={isLastAssistant}
              onRerun={rerunQuery}
              canRerun={!isRequestRunning}
            />
          );
        })}
      </MDBox>

      <Stack spacing={0.5} mt={1.5}>
        {visibleClarification ? (
          <ClarificationRenderer
            {...visibleClarification}
            onPick={(prompt) => sendPrompt(prompt)}
            onDismiss={() => setDismissedClarificationId(visibleClarification.clarificationId)}
          />
        ) : null}
        {trimmedInput.length > 500 ? (
          <Typography variant="caption" color="error.main" sx={{ px: 0.5 }}>
            {`${trimmedInput.length}/2000 characters used`}
          </Typography>
        ) : null}
        <Stack direction="row" spacing={1} alignItems="flex-end">
          <TextField
            multiline
            minRows={1}
            maxRows={4}
            fullWidth
            value={input}
            onChange={(event) => setInput(event.target.value)}
            placeholder="e.g. Show claim count by status for last 30 days"
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                if (canSubmit) {
                  sendPrompt(input);
                }
              }
            }}
          />
          <Button
            variant="contained"
            onClick={() => sendPrompt(input)}
            disabled={!canSubmit}
            aria-label="Send message"
            sx={{
              flexShrink: 0,
              minWidth: 40,
              width: 40,
              height: 40,
              p: 0,
              borderRadius: "50%",
              color: white.main,
              "& .MuiSvgIcon-root": { color: white.main },
            }}
          >
            {isRequestRunning ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <SendRoundedIcon fontSize="small" sx={{ color: white.main }} />
            )}
          </Button>
        </Stack>
      </Stack>
    </Card>
  );

  if (mode === "page") {
    if (isFullscreen) {
      return <Box sx={getFullscreenContainerSx(miniSidenav)}>{panel}</Box>;
    }
    return panel;
  }

  if (!open) {
    return <AmyLauncherButton onClick={() => setOpen(true)} />;
  }

  return (
    <Box
      sx={
        isFullscreen
          ? getFullscreenContainerSx(miniSidenav)
          : {
              position: "fixed",
              bottom: 16,
              right: 16,
              zIndex: ASSISTANT_POPUP_Z_INDEX,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: "fit-content",
            }
      }
    >
      {panel}
    </Box>
  );
}
